import { Injectable } from '@angular/core';
import { createWarning } from '@core/helpers/create-validator.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { toNum } from '@shared/helpers/math.helper';
import { Store } from '@ngrx/store';
import { isAfter, isBefore, isEqual } from 'date-fns';
import { take } from 'rxjs';

import { Revisione53State } from '../../../store/revisione53.reducers';
import { revisione53DatiSecondariSelectors } from '../../../store/selectors/dati-secondari.selectors';
import { CkNormeValuesEnum } from '../../amm-controllo/common/consts/ckNormeValues.enum';
import { CkNormeValues } from '../../amm-controllo/common/models/amm-controllo.type';
import { getGroupByCkNorme, getTipoAmministrazioneFieldByCkNorme } from '../../amm-controllo/common/helpers/norme-ck-lookup.helper';
import { MyVerbale } from '../../../models/interfaces/revisione.interface';

@Injectable()
export class ConclusioniWarningsService {
    private readonly LEGGE_BILANCIO_2018_DATA_REVISIONE_DAL = new Date(2018, 0, 1);
    private readonly LEGGE_BILANCIO_2018_DATA_COMPILAZ = new Date(2018, 8, 1);
    private readonly LEGGE_BILANCIO_2018_DATA_COMPILAZ_SCADENZA_SRL = new Date(2019, 0, 1);

    private readonly PATRIMONIO_NETTO_DL3_2020_DATA_DA = new Date(2020, 3, 9);
    private readonly PATRIMONIO_NETTO_DL3_2020_DATA_A = new Date(2020, 11, 31);

    constructor(private store: Store<Revisione53State>) {}

    private toDate(value: string | Date | null | undefined): Date {
        return value instanceof Date ? value : new Date(value as string);
    }

    private isOnOrAfter(date: Date, compareDate: Date): boolean {
        return isAfter(date, compareDate) || isEqual(date, compareDate);
    }

    private isOnOrBefore(date: Date, compareDate: Date): boolean {
        return isBefore(date, compareDate) || isEqual(date, compareDate);
    }

    private getLeggeBilancio2018Checks(my_DataRevisioneDal: string, my_DataCompilaz: string) {
        const dataRevisioneDal = this.toDate(my_DataRevisioneDal);
        const dataCompilaz = this.toDate(my_DataCompilaz);

        return {
            dataRevisioneDalBefore: isBefore(dataRevisioneDal, this.LEGGE_BILANCIO_2018_DATA_REVISIONE_DAL),
            dataCompilazAfterOrEqual: this.isOnOrAfter(dataCompilaz, this.LEGGE_BILANCIO_2018_DATA_COMPILAZ),
        };
    }

    private shouldWarnAmministratoreUnico(
        my_CkNorme: string,
        expectedNorma: CkNormeValuesEnum,
        tipoAmministrazione: string,
        my_DataRevisioneDal: string,
        my_DataCompilaz: string
    ): boolean {
        const { dataRevisioneDalBefore, dataCompilazAfterOrEqual } = this.getLeggeBilancio2018Checks(my_DataRevisioneDal, my_DataCompilaz);

        return !(my_CkNorme !== expectedNorma || tipoAmministrazione !== '2' || dataRevisioneDalBefore || !dataCompilazAfterOrEqual);
    }

    private shouldWarnCdAMinimoTreComponenti(
        my_CkNorme: string,
        expectedNorma: CkNormeValues,
        tipoAmministrazione: string,
        my_DataRevisioneDal: string,
        my_DataCompilaz: string,
        componenti: unknown[]
    ): boolean {
        const { dataRevisioneDalBefore, dataCompilazAfterOrEqual } = this.getLeggeBilancio2018Checks(my_DataRevisioneDal, my_DataCompilaz);

        return !(my_CkNorme !== expectedNorma || tipoAmministrazione !== '1' || dataRevisioneDalBefore || !dataCompilazAfterOrEqual || (componenti?.length ?? 0) >= 3);
    }

    private shouldWarnConsiglioGestioneMinimoTreComponenti(
        my_CkNorme: string,
        expectedNorma: CkNormeValuesEnum,
        my_DataRevisioneDal: string,
        my_DataCompilaz: string,
        componenti: unknown[]
    ): boolean {
        const { dataRevisioneDalBefore, dataCompilazAfterOrEqual } = this.getLeggeBilancio2018Checks(my_DataRevisioneDal, my_DataCompilaz);

        const numeroComponenti = componenti?.length ?? 0;

        return my_CkNorme === expectedNorma && !dataRevisioneDalBefore && dataCompilazAfterOrEqual && numeroComponenti > 0 && numeroComponenti < 3;
    }

    continuitaNelleRevisioni = createWarning(this.store, () => {
        let msg: string | null = null;

        this.store
            .select(revisione53DatiSecondariSelectors.getAEIncarico)
            .pipe(take(1))
            .subscribe((res) => {
                if (res?.[0]?.continuitaNelleRevisioni !== 'S') {
                    msg = `Attenzione, verificare e allegare al verbale la situazione contributiva come da sito MiSE. Nell'impossibilità di recuperare richiedere documenti attestanti versamenti effettuati o, in caso di mancata dimostrazione, dichiarazione sostitutiva di atto notorio sottoscritta dal legale rappresentante`;
                }
            });

        return msg;
    });

    ckFunzionamPEC = createWarning(this.store, (myVerbale) =>
        myVerbale.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_CkFunzionamPEC === '0'
            ? "PEC: se la PEC della cooperativa non è univoca ed attiva non sarà possibile rilasciare l'attestato di revisione"
            : null
    );

    ckScopoMutual = createWarning(this.store, (myVerbale) =>
        myVerbale.my_SezioneI__Rilevazione.my_NaturaMutualistica.my_CkScopoMutual === '0'
            ? "Scopo Mutualistico: se lo statuto non prevede lo scopo mutualistico non sarà possibile rilasciare l'attestato di revisione"
            : null
    );

    dataTermine = createWarning(this.store, (myVerbale) => {
        const my_DataTermine = myVerbale.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_DataTermine;
        const my_DataCompilaz = myVerbale.my_SezioneI__Rilevazione.my_Conclusioni.my_DataCompilaz;
        const my_CkScadenzaSocieta = myVerbale.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_CkScadenzaSocieta;
        const my_DataAssembleaLiquidazVolontaria = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_DataAssembleaLiquidazVolontaria;

        const shouldWarn = !(
            isBefore(this.toDate(my_DataCompilaz), this.toDate(my_DataTermine)) ||
            toNum(my_CkScadenzaSocieta) !== 0 ||
            !isNilOrEmpty(my_DataAssembleaLiquidazVolontaria)
        );

        return shouldWarn ? 'Attenzione: verificare la data di termine della società' : null;
    });

    ckScadenzaSocieta = createWarning(this.store, (myVerbale) => {
        const my_CkScadenzaSocieta = myVerbale.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_CkScadenzaSocieta;
        return my_CkScadenzaSocieta === '1' ? 'Termine della società: verificare la condizione statutaria del recesso soci' : null;
    });

    scSocObbl = createWarning(this.store, (myVerbale) => {
        const my_ScSocObbl = myVerbale.my_SezioneI__Rilevazione.my_SchedaCoopSociali.my_ScSocObbl;
        const my_CkIscrizAlboCoopMutP = myVerbale.my_SezioneI__Rilevazione.my_NaturaMutualistica.my_SchedaPrevalenza.my_CkIscrizAlboCoopMutP;
        const annoValue = toNum(myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_BilAnno1) - 1;
        const shouldWarn = !(toNum(my_ScSocObbl) === 1 || (toNum(my_CkIscrizAlboCoopMutP) !== 1 && toNum(my_CkIscrizAlboCoopMutP) !== 4));

        return shouldWarn ? `Attenzione: controllare la % di prevalenza nel ${annoValue}` : null;
    });

    tipoSezEnte = createWarning(this.store, (myVerbale) => {
        const my_TipoSezEnte = myVerbale.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_TipoSezEnte;
        const my_SociGiuCoopData = myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_SociGiuCoopData;
        const my_CkNormeSocioLavorat = myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_CkNormeSocioLavorat;

        const shouldWarn = !(
            my_TipoSezEnte !== '5' ||
            isNilOrEmpty(my_SociGiuCoopData) ||
            isNilOrEmpty(my_CkNormeSocioLavorat) ||
            toNum(my_SociGiuCoopData) <= 0 ||
            my_CkNormeSocioLavorat === '0'
        );

        return shouldWarn ? 'Attenzione: verificare la presenza di soci cooperatori persone giuridiche' : null;
    });

    myIdEsitoUltimaRevisione = createWarning(this.store, (myVerbale) => {
        const my_id_esito_ultima_revisione = myVerbale.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_id_esito_ultima_revisione;
        const my_CkProposte = myVerbale.my_SezioneI__Rilevazione.my_Conclusioni.my_CkProposte;

        const shouldWarn = !(my_id_esito_ultima_revisione === '0' || my_id_esito_ultima_revisione === '100' || my_CkProposte !== '1');

        return shouldWarn
            ? 'Attenzione: relazionare dettagliatamente sul superamento delle irregolarità che hanno comportato la richiesta di provvedimento sanzionatorio nella precedente revisione.'
            : null;
    });

    sociFisCoopData = createWarning(this.store, (myVerbale) => {
        const my_SociFisCoopData = myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_SociFisCoopData;
        const my_SociGiuCoopData = myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_SociGiuCoopData;
        const my_SociSpecData_personeFisiche = myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_gr0001.my_SociSpecData_personeFisiche;
        const somma1 = toNum(my_SociFisCoopData) + toNum(my_SociGiuCoopData) + toNum(my_SociSpecData_personeFisiche);
        const shouldWarn = !(
            isNilOrEmpty(my_SociFisCoopData) ||
            isNilOrEmpty(my_SociGiuCoopData) ||
            isNilOrEmpty(my_SociSpecData_personeFisiche) ||
            toNum(my_SociGiuCoopData) <= 0 ||
            somma1 >= 9
        );

        return shouldWarn ? 'Attenzione: verificare il numero di soci cooperatori' : null;
    });

    ckSociSpecDiritti = createWarning(this.store, (myVerbale) => {
        const my_CkSociSpecDiritti = myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_CkSociSpecDiritti;
        const my_SociSpecData = myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_SociSpecData;

        const shouldWarn = !(isNilOrEmpty(my_CkSociSpecDiritti) || my_CkSociSpecDiritti === '1' || toNum(my_SociSpecData) <= 0);

        return shouldWarn ? 'Soci Cat Speciale: il revisore verifichi i termini degli attuali soci iscritti alla categoria speciale' : null;
    });

    totConfSociFin = createWarning(this.store, (myVerbale) => {
        const my_TotConfSociFin = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_TotConfSociFin;
        const my_CapSocSottoscrSecondoEs = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CapSocSottoscrSecondoEs;

        const shouldWarn = !(isNilOrEmpty(my_TotConfSociFin) || my_TotConfSociFin <= my_CapSocSottoscrSecondoEs);

        return shouldWarn ? `Soci Finanziatori: il conferimento dei soci finanziatori è maggiore del capitale sottoscritto` : null;
    });

    capSocVersatoSecondoEs = createWarning(this.store, (myVerbale) => {
        const my_CapSocVersatoSecondoEs = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CapSocVersatoSecondoEs;
        const my_CapSocSottoscrSecondoEs = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CapSocSottoscrSecondoEs;

        const shouldWarn = !(
            toNum(my_CapSocVersatoSecondoEs) >= toNum(my_CapSocSottoscrSecondoEs) ||
            isNilOrEmpty(my_CapSocSottoscrSecondoEs) ||
            isNilOrEmpty(my_CapSocVersatoSecondoEs)
        );

        return shouldWarn ? 'Attenzione: Verificare le condizioni del capitale sociale ed eventualmente suggerire alla cooperativa il versamento' : null;
    });

    ckPrestitoSoci = createWarning(this.store, (myVerbale) => {
        const my_CkPrestitoSoci = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CkPrestitoSoci;
        const my_CapSocSottoscrSecondoEs = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CapSocSottoscrSecondoEs;
        const my_CapSocVersatoSecondoEs = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CapSocVersatoSecondoEs;
        const my_StPaAtAtot2 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_StatoPatrimoniale__Attivo.my_StPaAtAtot2;
        const my_NumBilanciInExcel = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_NumBilanciInExcel;

        const shouldWarn = !(
            my_CkPrestitoSoci !== '1' ||
            isNilOrEmpty(my_CapSocVersatoSecondoEs) ||
            isNilOrEmpty(my_CapSocSottoscrSecondoEs) ||
            (my_CkPrestitoSoci === '1' && toNum(my_StPaAtAtot2) <= 0) ||
            isNilOrEmpty(my_NumBilanciInExcel) ||
            toNum(my_NumBilanciInExcel) < 1
        );

        return shouldWarn ? 'Attenzione: verificare i termini di versamento del capitale sottoscritto da parte dei soci' : null;
    });

    ckNormeSRL = createWarning(this.store, (myVerbale) => {
        const my_CkNorme = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_CkNorme;
        const my_CkSrlTipoAmministrazione = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_NormeSrl.my_CkSrlTipoAmministrazione;
        const my_DataRevisioneDal = myVerbale.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_DataRevisioneDal;
        const my_DataCompilaz = myVerbale.my_SezioneI__Rilevazione.my_Conclusioni.my_DataCompilaz;

        const shouldWarn = this.shouldWarnAmministratoreUnico(my_CkNorme, CkNormeValuesEnum.normeSrl, my_CkSrlTipoAmministrazione, my_DataRevisioneDal, my_DataCompilaz);

        return shouldWarn
            ? `SRL: per le modifiche introdotte dalla legge di bilancio 2018, l'amministrazione della cooperativa non può essere affidata ad un amministratore unico`
            : null;
    });

    ckNormeSPO = createWarning(this.store, (myVerbale) => {
        const my_CkNorme = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_CkNorme;
        const my_CkSpoTipoAmministrazione = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_NormeSpaOrdinario.my_CkSpoTipoAmministrazione;
        const my_DataRevisioneDal = myVerbale.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_DataRevisioneDal;
        const my_DataCompilaz = myVerbale.my_SezioneI__Rilevazione.my_Conclusioni.my_DataCompilaz;

        const shouldWarn = this.shouldWarnAmministratoreUnico(my_CkNorme, CkNormeValuesEnum.normeSpaOrdinario, my_CkSpoTipoAmministrazione, my_DataRevisioneDal, my_DataCompilaz);

        return shouldWarn
            ? `SPO: per le modifiche introdotte dalla legge di bilancio 2018, l'amministrazione della cooperativa non può essere affidata ad un amministratore unico`
            : null;
    });

    CdAI = createWarning(this.store, (myVerbale) => {
        return this._CdAHelper(myVerbale, CkNormeValuesEnum.normeSrl);
    });

    CdAII = createWarning(this.store, (myVerbale) => {
        return this._CdAHelper(myVerbale, CkNormeValuesEnum.normeSpaOrdinario);
    });

    CdAIII = createWarning(this.store, (myVerbale) => {
        return this._CdAHelper(myVerbale, CkNormeValuesEnum.normeSpaMonistico);
    });

    ckNormeII = createWarning(this.store, (myVerbale) => {
        const my_CkNorme = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_CkNorme;
        const my_DataRevisioneDal = myVerbale.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_DataRevisioneDal;
        const my_DataCompilaz = myVerbale.my_SezioneI__Rilevazione.my_Conclusioni.my_DataCompilaz;
        const my_SpdElemConsiglioGestione =
            myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_NormeSpaDualistico.my_SpdConsiglioGestione.my_SpdElemConsiglioGestione;

        const shouldWarn = this.shouldWarnConsiglioGestioneMinimoTreComponenti(
            my_CkNorme,
            CkNormeValuesEnum.normeSpaDualistico,
            my_DataRevisioneDal,
            my_DataCompilaz,
            my_SpdElemConsiglioGestione
        );

        return shouldWarn
            ? `Consiglio di Gestione: per le modifiche introdotte dalla legge di bilancio 2018, l'amministrazione della cooperativa non può essere affidata ad un Consiglio di Gestione con meno di tre componenti`
            : null;
    });

    ckNormeIII = createWarning(this.store, (myVerbale) => {
        const my_CkNorme = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_CkNorme;
        const my_DataRevisioneDal = myVerbale.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_DataRevisioneDal;
        const my_DataCompilaz = myVerbale.my_SezioneI__Rilevazione.my_Conclusioni.my_DataCompilaz;
        const my_CkSrlTipoAmministrazione = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_NormeSrl.my_CkSrlTipoAmministrazione;
        const my_SrlScadenzaAmministratori = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_NormeSrl.my_SrlScadenzaAmministratori;

        const { dataRevisioneDalBefore } = this.getLeggeBilancio2018Checks(my_DataRevisioneDal, my_DataCompilaz);
        const dataCompilazAfterOrEqual2019 = this.isOnOrAfter(this.toDate(my_DataCompilaz), this.LEGGE_BILANCIO_2018_DATA_COMPILAZ_SCADENZA_SRL);

        const shouldWarn = !(
            my_CkNorme !== CkNormeValuesEnum.normeSrl ||
            dataRevisioneDalBefore ||
            !dataCompilazAfterOrEqual2019 ||
            toNum(my_SrlScadenzaAmministratori) !== 99 ||
            my_CkSrlTipoAmministrazione === '3'
        );

        return shouldWarn ? `Organo amministrativo per le modifiche introdotte dalla legge di bilancio 2018, la cooperativa deve definire un termine della nomina` : null;
    });

    ckScadenzaOrgAmm = createWarning(this.store, (myVerbale) => {
        const my_CkScadenzaOrgAmm = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_CkScadenzaOrgAmm;
        const my_AllConsiglioAmministrazione = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_AllConsiglioAmministrazione;

        const shouldWarn = !(toNum(my_CkScadenzaOrgAmm) === -1 || !my_AllConsiglioAmministrazione?.my_AllElemConsiglioAmministrazione?.length);

        return shouldWarn ? 'Verificare la scadenza del CDA' : null;
    });

    ckNormeIV = createWarning(this.store, (myVerbale) => {
        const my_CkNorme = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_CkNorme;
        const my_CalcObbOrganoContr = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_CalcObbOrganoContr;
        const my_CkSpoAssembNominSind = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_NormeSpaOrdinario.my_CkSpoAssembNominSind;

        const shouldWarn = !(my_CkNorme !== CkNormeValuesEnum.normeSpaOrdinario || my_CalcObbOrganoContr !== 'true' || my_CkSpoAssembNominSind !== '0');

        return shouldWarn ? `SPO: La cooperativa deve avere l'organo di controllo` : null;
    });

    ckNormeV = createWarning(this.store, (myVerbale) => {
        const my_CkNorme = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_CkNorme;
        const my_CalcObbOrganoContr = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_CalcObbOrganoContr;
        const my_CkSrlAssembNominSind = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_NormeSrl.my_CkSrlAssembNominSind;

        const shouldWarn = !(my_CkNorme !== CkNormeValuesEnum.normeSrl || my_CalcObbOrganoContr !== 'true' || my_CkSrlAssembNominSind !== '0');

        return shouldWarn ? `SRL: La cooperativa deve avere l'organo di controllo` : null;
    });

    importoPatrSecondoEs = createWarning(this.store, (myVerbale) => {
        const my_ImportoPatrSecondoEs = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_ImportoPatrSecondoEs;
        const dataChiusura2 = this.toDate(myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_DataChiusura2);

        const isBeforeOrEqual = this.isOnOrBefore(dataChiusura2, this.PATRIMONIO_NETTO_DL3_2020_DATA_A);
        const isAfterOrEqual = this.isOnOrAfter(dataChiusura2, this.PATRIMONIO_NETTO_DL3_2020_DATA_DA);

        const shouldWarn = !(toNum(my_ImportoPatrSecondoEs) >= 0 || isNilOrEmpty(my_ImportoPatrSecondoEs) || (isBeforeOrEqual && isAfterOrEqual));

        return shouldWarn ? `Attenzione, il patrimonio netto è negativo` : null;
    });

    importoPatrSecondoEsII = createWarning(this.store, (myVerbale) => {
        const my_ImportoPatrSecondoEs = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_ImportoPatrSecondoEs;
        const dataChiusura2 = this.toDate(myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_DataChiusura2);

        const isBeforeOrEqual = this.isOnOrBefore(dataChiusura2, this.PATRIMONIO_NETTO_DL3_2020_DATA_A);
        const isAfterOrEqual = this.isOnOrAfter(dataChiusura2, this.PATRIMONIO_NETTO_DL3_2020_DATA_DA);

        const shouldWarn = toNum(my_ImportoPatrSecondoEs) < 0 && !isNilOrEmpty(my_ImportoPatrSecondoEs) && isBeforeOrEqual && isAfterOrEqual;

        return shouldWarn ? `Attenzione, si valuti il patrimonio netto negativo insieme a quanto stabilito da art. 6 D.L. 8 Aprile 2020 n.3` : null;
    });

    private _CdAHelper(myVerbale: MyVerbale, ckExpected: CkNormeValues) {
        const my_CkNorme = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_CkNorme;
        const tipoAmministrazioneGroup = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo[getGroupByCkNorme(ckExpected)];
        const tipoAmministrazione = tipoAmministrazioneGroup[getTipoAmministrazioneFieldByCkNorme(ckExpected)];
        const my_DataRevisioneDal = myVerbale.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_DataRevisioneDal;
        const my_DataCompilaz = myVerbale.my_SezioneI__Rilevazione.my_Conclusioni.my_DataCompilaz;
        const my_AllElemConsiglioAmministrazione = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_AllConsiglioAmministrazione.my_AllElemConsiglioAmministrazione;

        const shouldWarn = this.shouldWarnCdAMinimoTreComponenti(
            my_CkNorme,
            ckExpected,
            tipoAmministrazione,
            my_DataRevisioneDal,
            my_DataCompilaz,
            my_AllElemConsiglioAmministrazione
        );

        return shouldWarn
            ? `CDA: per le modifiche introdotte dalla legge di bilancio 2018, l'amministrazione della cooperativa non può essere affidata ad un CdA con meno di tre componenti`
            : null;
    }
}
